import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { PremiumFeatureLock } from '../../components/premium/PremiumFeatureLock';
import { Card, Row, Col, Form, Button, Table, Modal } from 'react-bootstrap';
import { getDataOnRealtimeDb, updateDataOnRealtimeDb, deleteDataOnRealtimeDb, saveDataOnRealtimeDb } from '../../utils/firebase';
import axios from 'axios';
import { Spinner, Alert } from 'react-bootstrap';
import { MdAttachMoney, MdInfo, MdAccountCircle, MdTrendingDown, MdTrendingUp, MdCalendarToday, MdTableChart, MdError } from 'react-icons/md';
import { FaChartLine } from 'react-icons/fa';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Validation schemas
const debtSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    creditor: Yup.string().required('Required'),
    amount: Yup.number().required('Required'),
    dueDate: Yup.string().required('Required'),
    installment: Yup.number().required('Required'),
    interest: Yup.number().required('Required'),
    monthy_Installment_payment: Yup.number(),
    description: Yup.string(),
});

const repaymentSchema = Yup.object().shape({
    debt: Yup.string().required('Required'),
    amount: Yup.number().required('Required'),
    date: Yup.string().required('Required'),
    description: Yup.string(),
});

export default function DebtStrategy() {
    const { currentUser, subscription } = useAuth();
    const isPremium = subscription === 'premium';
    const [debts, setDebts] = useState([]);
    const [repayments, setRepayments] = useState([]);
    const [debtForm, setDebtForm] = useState({
        name: '', creditor: '', amount: '', dueDate: '', installment: '', interest: '', monthy_Installment_payment: '', description: ''
    });
    const [repaymentForm, setRepaymentForm] = useState({
        debt: '', amount: '', date: '', description: ''
    });
    const [editingDebtId, setEditingDebtId] = useState(null);
    const [editingRepaymentId, setEditingRepaymentId] = useState(null);
    const [optimizing, setOptimizing] = useState(false);
    const [debtStrategy, setDebtStrategy] = useState(null);
    const [error, setError] = useState(null);
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [showRepaymentModal, setShowRepaymentModal] = useState(false);
    const [enableCustomMonthlyInstallment, setEnableCustomMonthlyInstallment] = useState(false);
    const [monthlyBudget, setMonthlyBudget] = useState('');


    // Fetch debts and repayments from Firebase
    useEffect(() => {
        if (!currentUser) return;

        // Fetch debts
        getDataOnRealtimeDb(`users/${currentUser.uid}/debts`).then(debtsData => {
            if (!debtsData) return setDebts([]);
            if (Array.isArray(debtsData)) return setDebts(debtsData);
            if (typeof debtsData === 'object') {
                const debtsArray = Object.keys(debtsData).map(key => ({
                    id: key,
                    ...debtsData[key],
                }));
                return setDebts(debtsArray);
            }
            setDebts([]);
        });

        // Fetch repayments
        getDataOnRealtimeDb(`users/${currentUser.uid}/repayments`).then(repaymentsData => {
            if (!repaymentsData) return setRepayments([]);
            if (Array.isArray(repaymentsData)) return setRepayments(repaymentsData);
            if (typeof repaymentsData === 'object') {
                const repaymentsArray = Object.keys(repaymentsData).map(key => ({
                    id: key,
                    ...repaymentsData[key],
                }));
                return setRepayments(repaymentsArray);
            }
            setRepayments([]);
        });

    }, [currentUser]);


    useEffect(() => {
        const totalMinPayments = getTotalMonthlyPayment();
        // Add a small buffer to ensure it's enough to cover minimum payments
        const recommendedBudget = (parseFloat(totalMinPayments) + 0.1).toFixed(2);
        console.log("Recommended budget:", recommendedBudget);
        setMonthlyBudget(recommendedBudget);
    }, [debts]);

    // Handle Debt Form
    const handleDebtSubmit = async (values, { resetForm }) => {
        try {
            const { id, ...debtToUpdate } = values;
            await saveDataOnRealtimeDb(`users/${currentUser.uid}/debts`, debtToUpdate);

            // Fetch fresh data
            const debtsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/debts`);
            if (debtsData) {
                const debtsArray = Object.keys(debtsData).map(key => ({
                    id: key,
                    ...debtsData[key],
                }));
                setDebts(debtsArray);
            }
            toast.success('Debt added successfully');
            setShowDebtModal(false);
            resetForm();
        } catch (err) {
            console.error('Error updating debt:', err);
        }
    };

    // Handle Repayment Form
    const handleRepaymentSubmit = async (values, { resetForm, setFieldValue }) => {
        try {
            const { id, ...repaymentToUpdate } = values;
            await saveDataOnRealtimeDb(`users/${currentUser.uid}/repayments`, repaymentToUpdate);

            // Fetch fresh data
            const repaymentsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/repayments`);
            if (repaymentsData) {
                const repaymentsArray = Object.keys(repaymentsData).map(key => ({
                    id: key,
                    ...repaymentsData[key],
                }));
                setRepayments(repaymentsArray);
            }
            toast.success('Repayment added successfully');
            setShowRepaymentModal(false);
            resetForm();
            setFieldValue('monthy_Installment_payment', '');
        } catch (err) {
            console.error('Error updating repayment:', err);
        }
    };

    const handleEditDebt = async (debt) => {
        setEditingDebtId(debt.id);
        setDebtForm(debt);
        setShowDebtModal(true);
    };

    const handleEditRepayment = async (repayment) => {
        setEditingRepaymentId(repayment.id);
        setRepaymentForm(repayment);
        setShowRepaymentModal(true);
    };

    // Edit Debt
    const handleSaveDebt = async (debt, { resetForm }) => {
        try {
            const { id, ...debtToUpdate } = debt;
            await updateDataOnRealtimeDb(`users/${currentUser.uid}/debts`, id, debtToUpdate);

            // Fetch fresh data
            const debtsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/debts`);
            if (debtsData) {
                const debtsArray = Object.keys(debtsData).map(key => ({
                    id: key,
                    ...debtsData[key],
                }));
                setDebts(debtsArray);
            }
            toast.success('Debt updated successfully');
            setShowDebtModal(false);
            setEditingDebtId(null);
            resetForm();
        } catch (err) {
            console.error('Error updating debt:', err);
        }
    };

    // Edit Repayment
    const handleSaveRepayment = async (repayment, { resetForm }) => {
        try {
            const { id, ...repaymentToUpdate } = repayment;
            await updateDataOnRealtimeDb(`users/${currentUser.uid}/repayments`, id, repaymentToUpdate);

            // Fetch fresh data
            const repaymentsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/repayments`);
            if (repaymentsData) {
                const repaymentsArray = Object.keys(repaymentsData).map(key => ({
                    id: key,
                    ...repaymentsData[key],
                }));
                setRepayments(repaymentsArray);
            }
            toast.success('Repayment updated successfully');
            setShowRepaymentModal(false);
            setEditingRepaymentId(null);
            resetForm();
        } catch (err) {
            console.error('Error updating repayment:', err);
        }
    };

    // Delete Debt
    const handleDeleteDebt = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/debts`, id);

            // Fetch fresh data
            const debtsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/debts`);
            if (debtsData) {
                const debtsArray = Object.keys(debtsData).map(key => ({
                    id: key,
                    ...debtsData[key],
                }));
                setDebts(debtsArray);
            } else {
                setDebts([]);
            }
        } catch (err) {
            console.error('Error deleting debt:', err);
        }
    };

    // Delete Repayment
    const handleDeleteRepayment = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/repayments`, id);

            // Fetch fresh data
            const repaymentsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/repayments`);
            if (repaymentsData) {
                const repaymentsArray = Object.keys(repaymentsData).map(key => ({
                    id: key,
                    ...repaymentsData[key],
                }));
                setRepayments(repaymentsArray);
            } else {
                setRepayments([]);
            }
        } catch (err) {
            console.error('Error deleting repayment:', err);
        }
    };

    const renderPaymentPlan = (plan, totalMonths, totalPaid, methodName) => {
        // If plan is undefined or not an array, show average monthly payment
        if (!plan || !Array.isArray(plan)) {
            const monthlyPayment = totalPaid / totalMonths;
            return (
                <tr>
                    <td colSpan={2} className="text-center">
                        <div className="alert alert-info mb-0">
                            <small>
                                <strong>Average Monthly Payment:</strong> ${monthlyPayment.toFixed(2)}
                            </small>
                            <div className="mt-2">
                                <small className="text-muted">
                                    With the {methodName} method, you'll pay approximately ${monthlyPayment.toFixed(2)} 
                                    each month for {totalMonths} months.
                                </small>
                            </div>
                        </div>
                    </td>
                </tr>
            );
        }
        
        // If plan is available, render it with more details
        return plan.map((month, monthIndex) => (
            <tr key={monthIndex}>
                <td>Month {monthIndex + 1}</td>
                <td>
                    {Object.entries(month).map(([debtName, payment], debtIndex) => (
                        <div key={debtIndex} className="mb-1 pb-1" style={debtIndex < Object.entries(month).length - 1 ? {borderBottom: '1px dashed #eee'} : {}}>
                            <div className="d-flex justify-content-between">
                                <strong>{debtName}:</strong> 
                                <span className="text-success">${payment.toFixed(2)}</span>
                            </div>
                            {methodName === 'Avalanche' && debtIndex === 0 && Object.entries(month).length > 1 && (
                                <small className="text-muted d-block mt-1">
                                    Highest interest debt gets extra payment
                                </small>
                            )}
                            {methodName === 'Snowball' && debtIndex === 0 && Object.entries(month).length > 1 && (
                                <small className="text-muted d-block mt-1">
                                    Smallest balance debt gets extra payment
                                </small>
                            )}
                        </div>
                    ))}
                    <div className="mt-2 pt-1 border-top">
                        <small className="text-primary">
                            <strong>Total for Month {monthIndex + 1}:</strong> $
                            {Object.values(month).reduce((sum, payment) => sum + payment, 0).toFixed(2)}
                        </small>
                    </div>
                </td>
            </tr>
        ));
    };

    const getTotalMonthlyPayment = () => {
        return debts.reduce((total, debt) => total + Number(debt.monthy_Installment_payment), 0).toFixed(2);
    };

    const handleOptimizeDebt = async () => {
        if (!monthlyBudget || isNaN(parseFloat(monthlyBudget)) || parseFloat(monthlyBudget) <= 0) {
            setError('Please enter a valid monthly budget for debt repayment');
            return;
        }
        
        const totalMinPayments = getTotalMonthlyPayment();
        if (parseFloat(monthlyBudget) < parseFloat(totalMinPayments)) {
            setError(`Your budget (${parseFloat(monthlyBudget).toFixed(2)}) is less than the total minimum payments required (${totalMinPayments}). Please increase your budget.`);
            return;
        }

        const formattedDebts = debts.map(debt => ({
            name: debt.name,
            balance: debt.amount,
            interest_rate: Number(debt.interest / 100),
            min_payment: Number(debt.monthy_Installment_payment)
        }));

        const data = {
            debts: formattedDebts,
            monthly_budget: parseFloat(monthlyBudget)
        };

        try {
            setError(null);
            setOptimizing(true);

            // const response = await axios.post(`${process.env.REACT_APP_SECOND_PYTHON_APP}/optimize-debt`, data);

            const response = await axios.post(`${process.env.REACT_APP_PYTHON_APP}/api/insights/debt`, data);

            if (response.status === 200 && response.data) {
                if (response.data.error) {
                    setError(response.data.error);
                    setDebtStrategy(null);
                } else {
                    setDebtStrategy(response.data);
                }
            } else {
                setError('Prediction or advice is unavailable.');
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error);
            } else {
                setError(err.message || 'An error occurred while fetching the prediction.');
            }
        } finally {
            setOptimizing(false);
        }
    };

    if (!isPremium) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="main-title mb-0">Debt Repayment Strategy</h4>
                    </div>

                    <PremiumFeatureLock
                        title="AI Debt Repayment Optimizer"
                        description="Get personalized debt repayment strategies and optimize your path to becoming debt-free. Upgrade to premium to access this feature."
                    />

                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 className="main-title mb-0">Debt Repayment Strategy</h4>
                </div>

                {/* Debt Table */}
                <h6 className="mt-5 mb-2">All Debts</h6>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Creditor</th>
                            <th>Amount</th>
                            <th>Due Date</th>
                            <th>Installment</th>
                            <th>Interest</th>
                            <th>Monthly Installment</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!debts || debts.length === 0) ? (
                            <tr>
                                <td colSpan={8} className="text-center text-muted">No debt data</td>
                            </tr>
                        ) : (
                            debts.map(d => (
                                <tr key={d.id}>
                                    <td>{d.name}</td>
                                    <td>{d.creditor}</td>
                                    <td>{d.amount}</td>
                                    <td>{d.dueDate}</td>
                                    <td>{d.installment}</td>
                                    <td>{d.interest}%</td>
                                    <td>{d.monthy_Installment_payment}</td>

                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="btn-icon"
                                                onClick={() => handleEditDebt(d)}
                                            >
                                                <i className="ri-pencil-line"></i>
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="btn-icon"
                                                onClick={() => handleDeleteDebt(d.id)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            )))}
                    </tbody>
                </Table>

                {/* Repayment History Table */}
                <h6 className="mt-5 mb-2">Repayment History</h6>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Debt</th>
                            <th>Amount</th>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!repayments || repayments.length === 0) ? (
                            <tr>
                                <td colSpan={5} className="text-center text-muted">No repayment data</td>
                            </tr>
                        ) : (
                            repayments.map(r => (
                                <tr key={r.id}>
                                    <td>{r.debt}</td>
                                    <td>{r.amount}</td>
                                    <td>{r.date}</td>
                                    <td>{r.description}</td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="btn-icon"
                                                onClick={() => handleEditRepayment(r)}
                                            >
                                                <i className="ri-pencil-line"></i>
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="btn-icon"
                                                onClick={() => handleDeleteRepayment(r.id)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <Row className="g-3 mt-5">
                    <Col md={6}>
                        <Card>
                            <Card.Header>{editingDebtId ? 'Edit Debt' : 'Add Debt'}</Card.Header>
                            <Card.Body>
                                <Formik
                                    initialValues={editingDebtId ? debtForm : {
                                        name: '', creditor: '', amount: '', dueDate: '', installment: '', interest: '', description: '', monthy_Installment_payment: ''
                                    }}
                                    validationSchema={debtSchema}
                                    enableReinitialize
                                    onSubmit={handleDebtSubmit}
                                >
                                    {({ isSubmitting, setFieldValue, values }) => (
                                        <FormikForm>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Name</Form.Label>
                                                <Field name="name" className="form-control" />
                                                <ErrorMessage name="name" component="div" className="text-danger small" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Creditor</Form.Label>
                                                <Field name="creditor" className="form-control" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Amount</Form.Label>
                                                <Field name="amount" type="number" className="form-control" />
                                                <ErrorMessage name="amount" component="div" className="text-danger small" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Due Date</Form.Label>
                                                <Field name="dueDate" type="date" className="form-control" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Installment</Form.Label>
                                                <Field name="installment" type="number" className="form-control"
                                                    onChange={(e) => {
                                                        const installmentValue = parseFloat(e.target.value);
                                                        setFieldValue('installment', installmentValue);

                                                        if (installmentValue !== 0) {
                                                            setFieldValue('monthy_Installment_payment', values.amount / installmentValue);
                                                        }
                                                    }}
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-2">
                                                <Form.Label>Interest (%)</Form.Label>
                                                <Field name="interest" type="number" className="form-control"
                                                    onChange={(e) => {
                                                        const interestRate = parseFloat(e.target.value) / 100; // Convert percentage to decimal
                                                        const principal = values.amount; // Your loan amount
                                                        const months = values.installment; // Your installment period in months

                                                        setFieldValue('interest', e.target.value);

                                                        if (interestRate == 0) {
                                                            setFieldValue('monthy_Installment_payment', months * principal);
                                                        }

                                                        if (interestRate && principal && months) {
                                                            // Monthly interest rate calculation
                                                            const monthlyRate = interestRate / 12;
                                                            const monthlyInstallment = (
                                                                (principal * monthlyRate * (1 + monthlyRate) ** months) /
                                                                ((1 + monthlyRate) ** months - 1)
                                                            ).toFixed(2); // Round to 2 decimal places

                                                            setFieldValue('monthy_Installment_payment', monthlyInstallment);
                                                        }
                                                    }}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Check type="checkbox" name="enableCustomMonthlyInstallment" label="Enable Custom Monthly Installment" onChange={(e) => {
                                                    setEnableCustomMonthlyInstallment(e.target.checked);
                                                }} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Minimum Monthly Installment</Form.Label>
                                                <Field name="monthy_Installment_payment" type="number" className="form-control" disabled={!enableCustomMonthlyInstallment} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Description</Form.Label>
                                                <Field as="textarea" name="description" className="form-control" />
                                            </Form.Group>
                                            <Button type="submit" className="w-100 mt-2" disabled={isSubmitting}>
                                                {editingDebtId ? 'Update Debt' : 'Add Debt'}
                                            </Button>
                                        </FormikForm>
                                    )}
                                </Formik>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card>
                            <Card.Header>{editingRepaymentId ? 'Edit Repayment' : 'Add Debt Repayment'}</Card.Header>
                            <Card.Body>
                                <Formik
                                    initialValues={editingRepaymentId ? repaymentForm : {
                                        debt: '', amount: '', date: '', description: ''
                                    }}
                                    validationSchema={repaymentSchema}
                                    enableReinitialize
                                    onSubmit={handleRepaymentSubmit}
                                >
                                    {({ isSubmitting }) => (
                                        <FormikForm>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Debt</Form.Label>
                                                <Field as="select" name="debt" className="form-control">
                                                    <option value="">Select Debt</option>
                                                    {(debts || []).map(d => (
                                                        <option key={d.id} value={d.name}>{d.name}</option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="debt" component="div" className="text-danger small" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Amount</Form.Label>
                                                <Field name="amount" type="number" className="form-control" />
                                                <ErrorMessage name="amount" component="div" className="text-danger small" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Date</Form.Label>
                                                <Field name="date" type="date" className="form-control" />
                                                <ErrorMessage name="date" component="div" className="text-danger small" />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Description</Form.Label>
                                                <Field as="textarea" name="description" className="form-control" />
                                            </Form.Group>
                                            <Button type="submit" className="w-100 mt-2" disabled={isSubmitting}>
                                                {editingRepaymentId ? 'Update Repayment' : 'Log Repayment'}
                                            </Button>
                                        </FormikForm>
                                    )}
                                </Formik>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="mt-5 shadow-sm border-0">
                    <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0 d-flex align-items-center">
                            Optimize Your Debt Repayment Strategy
                        </h5>
                    </Card.Header>
                    <Card.Body className="bg-light">
                        <Row>
                            <Col md="12">
                                <div className="mb-4 p-4 bg-white rounded shadow-sm">
                                    <Alert variant="info" className="mb-0 border-0">
                                        <h6 className="alert-heading d-flex align-items-center">
                                            <MdInfo className="me-2" size={20} />
                                            How It Works
                                        </h6>
                                        <p className="mb-2">
                                            Our debt optimizer compares two popular debt repayment strategies:
                                        </p>
                                        <ul className="mb-0">
                                            <li><strong>Avalanche Method:</strong> Pay highest interest debts first (saves the most money)</li>
                                            <li><strong>Snowball Method:</strong> Pay smallest balances first (provides psychological wins)</li>
                                        </ul>
                                    </Alert>

                                    <h6 className="mb-3 text-primary mt-3">Monthly Budget for Debt Repayment</h6>
                                    <p className="text-muted mb-3">
                                        Enter the total amount you can allocate monthly towards paying off your debts.
                                        This will help us calculate the most efficient repayment strategy.
                                    </p>
                                    <div className="d-flex align-items-center">
                                        <div className="input-group mb-3" style={{ maxWidth: '300px' }}>
                                            <span className="input-group-text">$</span>
                                            <Form.Control
                                                type="number"
                                                placeholder="Enter monthly budget"
                                                value={monthlyBudget}
                                                onChange={(e) => setMonthlyBudget(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <Button
                                            variant="primary"
                                            onClick={handleOptimizeDebt}
                                            className="ms-3"
                                            disabled={optimizing}
                                        >
                                            {optimizing ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                    Calculating...
                                                </>
                                            ) : (
                                                <>
                                                    <FaChartLine className="me-2" />
                                                    Calculate Strategy
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                    {error && (
                                        <Alert variant="danger" className="mt-3">
                                            <MdError className="me-2" size={20} />
                                            {error}
                                        </Alert>
                                    )}
                                </div>



                                {debtStrategy && !optimizing && (
                                    <div className="results-section mt-4">
                                        <h5 className="mb-3 text-primary">Your Personalized Debt Repayment Plan</h5>
                                        <Row className="gy-4">
                                            {/* Avalanche Method Card */}
                                            <Col md={6}>
                                                <Card className="h-100 border-0 shadow-sm">
                                                    <Card.Header className="bg-gradient-primary">
                                                        <div className="d-flex align-items-center">
                                                            <div className="method-icon bg-white text-primary rounded-circle p-2 me-3">
                                                                <MdTrendingDown size={24} />
                                                            </div>
                                                            <div>
                                                                <h5 className="mb-0">Avalanche Method</h5>
                                                                <small>Pay highest interest first</small>
                                                            </div>
                                                        </div>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between mb-4">
                                                            <div className="text-center p-3 bg-light rounded">
                                                                <div className="text-muted small">Total Months</div>
                                                                <div className="fs-3 fw-bold text-primary">{debtStrategy.avalanche.total_months}</div>
                                                            </div>
                                                            <div className="text-center p-3 bg-light rounded">
                                                                <div className="text-muted small">Total Interest</div>
                                                                <div className="fs-3 fw-bold text-success">${debtStrategy.avalanche.total_interest.toFixed(2)}</div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-3 text-primary border-bottom pb-2">Payment Schedule</h6>
                                                            <div className="payment-schedule-table" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                <Table size="sm" bordered hover responsive>
                                                                    <thead className="bg-light">
                                                                        <tr>
                                                                            <th>Month</th>
                                                                            <th>Payments</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {renderPaymentPlan(
                                                                            debtStrategy.avalanche.payment_plan,
                                                                            debtStrategy.avalanche.total_months,
                                                                            debtStrategy.avalanche.total_paid,
                                                                            'Avalanche'
                                                                        )}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>

                                            {/* Snowball Method Card */}
                                            <Col md={6}>
                                                <Card className="h-100 border-0 shadow-sm">
                                                    <Card.Header className="bg-gradient-success">
                                                        <div className="d-flex align-items-center">
                                                            <div className="method-icon bg-white text-success rounded-circle p-2 me-3">
                                                                <MdTrendingUp size={24} />
                                                            </div>
                                                            <div>
                                                                <h5 className="mb-0">Snowball Method</h5>
                                                                <small>Pay smallest balance first</small>
                                                            </div>
                                                        </div>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between mb-4">
                                                            <div className="text-center p-3 bg-light rounded">
                                                                <div className="text-muted small">Total Months</div>
                                                                <div className="fs-3 fw-bold text-primary">{debtStrategy.snowball.total_months}</div>
                                                            </div>
                                                            <div className="text-center p-3 bg-light rounded">
                                                                <div className="text-muted small">Total Interest</div>
                                                                <div className="fs-3 fw-bold text-success">${debtStrategy.snowball.total_interest.toFixed(2)}</div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-3 text-success border-bottom pb-2">Payment Schedule</h6>
                                                            <div className="payment-schedule-table" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                <Table size="sm" bordered hover responsive>
                                                                    <thead className="bg-light">
                                                                        <tr>
                                                                            <th>Month</th>
                                                                            <th>Payments</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {renderPaymentPlan(
                                                                            debtStrategy.snowball.payment_plan,
                                                                            debtStrategy.snowball.total_months,
                                                                            debtStrategy.snowball.total_paid,
                                                                            'Snowball'
                                                                        )}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>

                                            {/* Recommendation Card */}
                                            <Col md={12} className="mt-3">
                                                <Card className="border-0 shadow-sm">
                                                    <Card.Body className="p-4">
                                                        <h5 className="card-title mb-3 text-center">
                                                            <span className="badge bg-primary me-2">Recommendation</span>
                                                            {debtStrategy.avalanche.total_interest <= debtStrategy.snowball.total_interest ?
                                                                "Use the Avalanche Method to save the most money" :
                                                                "Consider the Snowball Method for psychological wins"}
                                                        </h5>
                                                        <div className="d-flex justify-content-center gap-4 mb-3">
                                                            <div className="text-center">
                                                                <div className="text-muted small">Time Difference</div>
                                                                <div className="fs-5 fw-bold">
                                                                    {Math.abs(debtStrategy.avalanche.total_months - debtStrategy.snowball.total_months)} months
                                                                </div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-muted small">Interest Savings</div>
                                                                <div className="fs-5 fw-bold text-success">
                                                                    ${Math.abs(debtStrategy.avalanche.total_interest - debtStrategy.snowball.total_interest).toFixed(2)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <p className="text-muted text-center mb-0">
                                                            {debtStrategy.avalanche.total_interest <= debtStrategy.snowball.total_interest ?
                                                                "The Avalanche method will save you money by targeting high-interest debt first." :
                                                                "The Snowball method provides psychological wins by paying off small debts quickly."}
                                                        </p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Footer />
            </div>

            {/* Debt Modal */}
            <Modal show={showDebtModal} onHide={() => { setShowDebtModal(false); setEditingDebtId(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingDebtId ? 'Edit Debt' : 'Add Debt'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={editingDebtId ? debtForm : {
                            name: '', creditor: '', amount: '', dueDate: '', installment: '', interest: '', description: ''
                        }}
                        validationSchema={debtSchema}
                        enableReinitialize
                        onSubmit={handleSaveDebt}
                    >
                        {({ isSubmitting }) => (
                            <FormikForm>
                                <Form.Group className="mb-2">
                                    <Form.Label>Name</Form.Label>
                                    <Field name="name" className="form-control" />
                                    <ErrorMessage name="name" component="div" className="text-danger small" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Creditor</Form.Label>
                                    <Field name="creditor" className="form-control" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Amount</Form.Label>
                                    <Field name="amount" type="number" className="form-control" />
                                    <ErrorMessage name="amount" component="div" className="text-danger small" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Due Date</Form.Label>
                                    <Field name="dueDate" type="date" className="form-control" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Installment</Form.Label>
                                    <Field name="installment" type="number" className="form-control" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Interest (%)</Form.Label>
                                    <Field name="interest" type="number" className="form-control" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Description</Form.Label>
                                    <Field as="textarea" name="description" className="form-control" />
                                </Form.Group>
                                <Button type="submit" className="w-100 mt-2" disabled={isSubmitting}>
                                    {editingDebtId ? 'Update Debt' : 'Add Debt'}
                                </Button>
                            </FormikForm>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>

            {/* Repayment Modal */}
            <Modal show={showRepaymentModal} onHide={() => { setShowRepaymentModal(false); setEditingRepaymentId(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingRepaymentId ? 'Edit Repayment' : 'Add Debt Repayment'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={editingRepaymentId ? repaymentForm : {
                            debt: '', amount: '', date: '', description: ''
                        }}
                        validationSchema={repaymentSchema}
                        enableReinitialize
                        onSubmit={handleSaveRepayment}
                    >
                        {({ isSubmitting }) => (
                            <FormikForm>
                                <Form.Group className="mb-2">
                                    <Form.Label>Debt</Form.Label>
                                    <Field as="select" name="debt" className="form-control">
                                        <option value="">Select Debt</option>
                                        {(debts || []).map(d => (
                                            <option key={d.id} value={d.name}>{d.name}</option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="debt" component="div" className="text-danger small" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Amount</Form.Label>
                                    <Field name="amount" type="number" className="form-control" />
                                    <ErrorMessage name="amount" component="div" className="text-danger small" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Date</Form.Label>
                                    <Field name="date" type="date" className="form-control" />
                                    <ErrorMessage name="date" component="div" className="text-danger small" />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Description</Form.Label>
                                    <Field as="textarea" name="description" className="form-control" />
                                </Form.Group>
                                <Button type="submit" className="w-100 mt-2" disabled={isSubmitting}>
                                    {editingRepaymentId ? 'Update Repayment' : 'Log Repayment'}
                                </Button>
                            </FormikForm>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </React.Fragment>
    );
} 